import isEmpty from 'lodash/isEmpty';
import { WAITING_ROOM_TOKEN_HEADER } from '../../../api/base';
import { generateGuestToken } from '../../guest';
import {
    createOrder as createOrderApi,
    getOrder as getOrderApi,
    getOrderAttendees as getOrderAttendeesApi,
} from '../../../api/order';
import { getInitSurveysActionFromOrderResponse } from '../../survey';
import { DEFAULT_COUNTRY } from '../../../constants';
import { getVariantPromoCode } from '../../../utils/promoCodes';
import { getEventShippingSettings as getEventShippingSettingsAction } from '../../event';
import { startBackgroundTimer as startBackgroundTimerAction } from '../../timer';
import { updateWaitingRoomWithToken as updateWaitingRoomWithTokenAction } from '../../waitingRoom';
import { updateDonationFeeAndTax } from '../../tickets';
import { OrderActionType } from '../types';
import { hasDonationTickets } from '../../../containers/ticketSelection/utils/ticketUtils';
import { hasExistingOrder } from '../../../selectors/order/typedOrderSelectors';
import { getSelectedTickets } from '../../../selectors/tickets/getSelectedTickets';
import { isEventbriteTLD } from '../../../selectors/app';
import { track } from '@eventbrite/datalayer-library';
import { updateStatsigUserMetadata } from '../../../actions/statsig';
import { HeapEventName } from '../../../constants/heap';
import { updateOrderAttendeesDefaultDeliveryMethodByCountry } from './updateOrderAttendeesDefaultDeliveryMethodByCountry';
import { initializeOrder } from './initializeOrder';
import { getAndSaveAvailablePaymentMethods } from './getAndSaveAvailablePaymentMethods';
import { handleUpdateOrderToken } from './handleUpdateOrderToken';

const _checkAndUpdateDonationFeeAndTax =
    (response, selectedTickets) => async (dispatch) => {
        if (!hasDonationTickets(selectedTickets)) {
            return undefined;
        }

        return dispatch(updateDonationFeeAndTax(response));
    };

/**
 * Calls the relevant api to create / retrieve the order
 * In case no order exists (existingOrderId is null/undefined) we are in the standard flow, and we call the usual `createOrderApi` endpoint
 * If the order has previously been created (Add Attendees flow for instance) we just retrieve the order using the new `getOrderApi` endpoint
 *
 * @param {function} dispatch redux dispatch function
 * @param {Object} orderInfo object containing necessary info related to the order
 */
const _callOrderApi = async (dispatch, orderInfo) => {
    const { existingOrderId, eventId } = orderInfo;

    if (existingOrderId) {
        const { response, headers } = await getOrderApi(existingOrderId);

        return {
            orderResponse: {
                order: response,
                attendees: [],
                // Force fetching attendees in order to handle cases with > 50
                forceFetchAttendees: true,
            },
            orderHeaders: headers,
        };
    }

    await generateGuestToken(dispatch, eventId);

    const { response, headers } = await createOrderApi(orderInfo);

    return {
        orderResponse: response,
        orderHeaders: headers,
    };
};

export const processOrder = async (dispatch, getState) => {
    const {
        app: {
            affiliateCode,
            existingOrderId,
            inviteToken,
            campaignToken,
            waitlistCode,
        },
        event: { id: eventId, isReservedSeating },
        groupRegistration: { teamSettings: { token: teamToken } = {} } = {},
    } = getState();

    const state = getState();

    const isEvbTLD = isEventbriteTLD(state);
    const selectedTickets = getSelectedTickets(state);
    const promoCode = getVariantPromoCode(selectedTickets);
    const orderInfo = {
        affiliateCode,
        campaignToken,
        eventId,
        existingOrderId,
        isReservedSeating,
        inviteToken,
        promoCode,
        selectedTickets,
        teamToken,
        waitlistCode,
        isEventbriteTLD: isEvbTLD,
    };

    const { orderResponse, orderHeaders } = await _callOrderApi(
        dispatch,
        orderInfo,
    );

    const attendeesExpansions = ['survey'];

    if (hasExistingOrder(getState())) {
        attendeesExpansions.push('survey_responses');
    }

    if (isReservedSeating) {
        attendeesExpansions.push('ticket_class.reserved_seating');
    } else {
        attendeesExpansions.push('ticket_class');
    }

    const attendeesExpansionsString = attendeesExpansions.join(',');

    // When creating an "empty" order e.g. use Pick-a-seat to select ticket
    // the response returned from order summary is flattened, not having `order` root element.
    const orderData = orderResponse?.order || orderResponse;

    // This needs to happen before GET /order/:orderId/attendees fetch, because the request needs token setup.
    await handleUpdateOrderToken(dispatch, orderHeaders);

    // Create the order first, the response includes `attendees` get paginated and could have a limit to 50.
    // The expected approach is: after order created, paginate over order/:orderId/attendees to get all the attendees
    // This follows the same approach already implemented in organizer app
    const attendees = await getOrderAttendeesApi(
        orderData?.id,
        attendeesExpansionsString,
    );
    const orderStatePayload = {
        ...orderData,
        attendees,
    };

    await dispatch(getInitSurveysActionFromOrderResponse(orderStatePayload));
    await dispatch(initializeOrder(orderStatePayload));

    await dispatch(
        _checkAndUpdateDonationFeeAndTax(orderResponse, selectedTickets),
    );
    await dispatch(getEventShippingSettingsAction());
    await dispatch(
        updateOrderAttendeesDefaultDeliveryMethodByCountry(DEFAULT_COUNTRY),
    );

    // XXX: we are currently dispatching the getAndSaveAvailablePaymentMethods thunk in a couple
    // different places in the checkout flow where GA and Reserved diverged but we should only
    // have to do this at the last step before checkout (ie when the CheckoutPage is rendered).
    if (orderData && !isEmpty(attendees)) {
        await dispatch(getAndSaveAvailablePaymentMethods());
    }

    await dispatch(
        updateWaitingRoomWithTokenAction(
            orderHeaders.get(WAITING_ROOM_TOKEN_HEADER),
        ),
    );

    dispatch({
        type: OrderActionType.updateOrder,
        payload: {
            isPlaced: false,
        },
    });

    track({
        eventName: HeapEventName.createOrderSuccess,
        eventData: { orderId: orderData?.id },
    });

    dispatch(updateStatsigUserMetadata);

    return dispatch(startBackgroundTimerAction());
};
